import type { ReactNode } from "react";

import { cn } from "@utils/ui";

type ModalSectionProps = {
  title: string;
  description?: string;
  /** Rendered on the right of the header — e.g. a state badge or parse chip. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * A white card on the modal's grey canvas (MDRS-230 design).
 *
 * Used by the Update case modal to group its fields — Examinee and claim ·
 * Assessment · Schedule · Access — instead of one long flat form. Pair with
 * `ModalCanvas` for the recessed background.
 *
 * Single consumer today; the Create modal keeps its own step-based layout. Kept
 * here rather than inlined so the Create modal can adopt the same grouping without
 * copying it, but move it next to UpdateCaseModal if that never happens.
 */
export const ModalSection = ({
  title,
  description,
  aside,
  children,
  className,
}: ModalSectionProps) => (
  <section
    className={cn(
      "overflow-hidden rounded-xl border border-foreground-muted/20 bg-background-white shadow-sm",
      className
    )}
  >
    <div className="flex items-start justify-between gap-4 border-b border-foreground-muted/15 px-5 py-4">
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold tracking-[-0.01em]">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-foreground-muted">{description}</p>
        )}
      </div>
      {aside}
    </div>
    <div className="space-y-4 p-5">{children}</div>
  </section>
);

/** The recessed grey canvas the cards sit on. */
export const ModalCanvas = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "-mx-8 max-h-[70vh] space-y-4 overflow-y-auto bg-[#F6F8FB] px-8 py-5",
      className
    )}
  >
    {children}
  </div>
);
